/**
 * Qué rutas deja abrir el plan: la tabla que usa `PortonDelRolDrop` para redirigir, y que una pantalla
 * usa para no ofrecer un botón que el portón rebotaría.
 *
 * 🐛 POR QUÉ SALIÓ DEL PORTÓN (auditoría del 27-sep): la lista de Ventas le ofrecía «Resumen» a la
 *    tienda del plan Drop, y el portón la devolvía al Inicio — un botón que no sirve. La regla vivía
 *    sólo adentro del componente que redirige; ahora la leen los dos y no pueden discrepar.
 *
 * Un plan sin entrada no tiene portón: el equipo de Smarty ve todo con sus planes de siempre
 * (enterprise).
 *
 * 🪤 UN PLAN DE ROL MANDA AUNQUE LA CUENTA SEA INTERNA (29-sep). `is_internal` hacía dos trabajos
 *    —«el equipo ve todo» y «no cuenta en las métricas»— y Revendedor Demo, la cuenta con la que el
 *    founder mira el panel como un drop, al marcarse interna para no medirse pasó a ver el panel
 *    entero. El rol es un dato: quien tiene plan `drop` o `almacen` ve lo de su rol, siempre.
 */
import { laRutaEsDelRolDrop, A_DONDE_VA_EL_DROP } from './rutasDelRolDrop';
import { laRutaEsDelRolAlmacen, A_DONDE_VA_EL_ALMACEN } from './rutasDelRolAlmacen';
import { laRutaEsDelVendedorDrop, A_DONDE_VA_EL_VENDEDOR_DROP } from './rutasDelVendedorDrop';
import { laRutaEsDelSoloAlmacen, A_DONDE_VA_EL_SOLO_ALMACEN } from './rutasDelSoloAlmacen';

export interface PortonDelPlan { deja: (ruta: string) => boolean; vuelveA: string }

const PORTONES: Record<string, PortonDelPlan> = {
  drop: { deja: laRutaEsDelRolDrop, vuelveA: A_DONDE_VA_EL_DROP },
  almacen: { deja: laRutaEsDelRolAlmacen, vuelveA: A_DONDE_VA_EL_ALMACEN },
};

/**
 * Dentro de una cuenta Drop, el VENDEDOR tiene su propio portón, más chico que el del plan: sus ventas
 * y sus clientes (ver `rutasDelVendedorDrop`). Quién es vendedor lo dice `useSoyVendedorDelDrop`.
 */
const PORTON_DEL_VENDEDOR_DROP: PortonDelPlan = { deja: laRutaEsDelVendedorDrop, vuelveA: A_DONDE_VA_EL_VENDEDOR_DROP };
// El almacén que es SÓLO almacén (marca blanca, 23-oct): la lista corta. Ver `rutasDelSoloAlmacen`.
const PORTON_DEL_SOLO_ALMACEN: PortonDelPlan = { deja: laRutaEsDelSoloAlmacen, vuelveA: A_DONDE_VA_EL_SOLO_ALMACEN };

export function elPortonDelPlan(planType: string | null | undefined, quien?: { vendedor?: boolean; soloAlmacen?: boolean }): PortonDelPlan | undefined {
  if (planType === 'drop' && quien?.vendedor) return PORTON_DEL_VENDEDOR_DROP;
  if (planType === 'almacen' && quien?.soloAlmacen) return PORTON_DEL_SOLO_ALMACEN;
  return planType ? PORTONES[planType] : undefined;
}

/** Si el plan deja entrar a esa ruta (con o sin `/store/:slug` delante). */
export function elPlanDejaEntrarA(planType: string | null | undefined, ruta: string, quien?: { vendedor?: boolean; soloAlmacen?: boolean }): boolean {
  const porton = elPortonDelPlan(planType, quien);
  return !porton || porton.deja(ruta);
}
